import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { BarChart as BarChartIcon } from '@mui/icons-material';
import api from '../services/api';
import DateField from './DateField';

interface Group { male: number; female: number; unknown: number; total: number; }
interface SterilizationStats {
  shelter: Group;
  external: Group;
  totals: { male: number; female: number; unknown: number };
  grand_total: number;
  has_unknown: boolean;
}

const firstDayOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const StatisticsPage: React.FC = () => {
  const { t } = useTranslation();

  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [municipality, setMunicipality] = useState<string>('All');
  const [startDate, setStartDate] = useState<string>(firstDayOfMonth());
  const [endDate, setEndDate] = useState<string>(today());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<SterilizationStats | null>(null);

  const loadMunicipalities = useCallback(async () => {
    try {
      const response = await api.get('/api/tables/public/municipalities/data?limit=1000');
      setMunicipalities(response.data.rows.map((row: any[]) => row[0]).sort());
    } catch (e) {
      console.error('Error loading municipalities:', e);
    }
  }, []);

  useEffect(() => { loadMunicipalities(); }, [loadMunicipalities]);

  const handleCalculate = async () => {
    setError(null);
    if (!startDate || !endDate) { setError(t('statistics.selectPeriod')); return; }
    if (new Date(startDate) > new Date(endDate)) { setError(t('statistics.invalidPeriod')); return; }
    setLoading(true);
    try {
      const response = await api.post('/api/stats/sterilizations', {
        start_date: startDate,
        end_date: endDate,
        municipality,
      });
      setStats(response.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('statistics.failed'));
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const kpi = (label: string, value: number, color: string) => (
    <Grid item xs={6} sm={4} md={2.4}>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, textAlign: 'center', border: '1px solid #eee', borderTop: `3px solid ${color}` }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Paper>
    </Grid>
  );

  const showUnknown = !!stats?.has_unknown;

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <BarChartIcon sx={{ color: '#667eea' }} />
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#667eea' }}>
          {t('statistics.title')}
        </Typography>
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(102,126,234,0.12)' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <DateField label={t('report.startDate')} value={startDate} onChange={(v: string) => setStartDate(v)} />
          <DateField label={t('report.endDate')} value={endDate} onChange={(v: string) => setEndDate(v)} />
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>{t('cat.municipality')}</InputLabel>
            <Select label={t('cat.municipality')} value={municipality} onChange={(e) => setMunicipality(e.target.value)}>
              <MenuItem value="All">{t('report.allMunicipalities')}</MenuItem>
              {municipalities.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleCalculate} disabled={loading} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {loading ? <CircularProgress size={20} /> : t('statistics.calculate')}
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {stats && (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 4px 16px rgba(102,126,234,0.12)' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea', mb: 2 }}>
            {t('statistics.sterilizationsTitle')}
          </Typography>

          {stats.grand_total === 0 ? (
            <Typography color="text.secondary">{t('statistics.noData')}</Typography>
          ) : (
            <>
              {/* KPI tiles */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {kpi(t('statistics.kpiTotal'), stats.grand_total, '#667eea')}
                {kpi(t('statistics.male'), stats.totals.male, '#1976d2')}
                {kpi(t('statistics.female'), stats.totals.female, '#d81b60')}
                {kpi(t('statistics.shelter'), stats.shelter.total, '#ed6c02')}
                {kpi(t('statistics.external'), stats.external.total, '#2e7d32')}
              </Grid>

              {/* Matrix */}
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.light' }}>
                      <TableCell><strong>{t('statistics.category')}</strong></TableCell>
                      <TableCell align="right"><strong>{t('statistics.male')}</strong></TableCell>
                      <TableCell align="right"><strong>{t('statistics.female')}</strong></TableCell>
                      {showUnknown && <TableCell align="right"><strong>{t('statistics.unknown')}</strong></TableCell>}
                      <TableCell align="right"><strong>{t('statistics.total')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow hover>
                      <TableCell>{t('statistics.shelter')}</TableCell>
                      <TableCell align="right">{stats.shelter.male}</TableCell>
                      <TableCell align="right">{stats.shelter.female}</TableCell>
                      {showUnknown && <TableCell align="right">{stats.shelter.unknown}</TableCell>}
                      <TableCell align="right"><strong>{stats.shelter.total}</strong></TableCell>
                    </TableRow>
                    <TableRow hover>
                      <TableCell>{t('statistics.external')}</TableCell>
                      <TableCell align="right">{stats.external.male}</TableCell>
                      <TableCell align="right">{stats.external.female}</TableCell>
                      {showUnknown && <TableCell align="right">{stats.external.unknown}</TableCell>}
                      <TableCell align="right"><strong>{stats.external.total}</strong></TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'primary.light' }}>
                      <TableCell><strong>{t('statistics.total')}</strong></TableCell>
                      <TableCell align="right"><strong>{stats.totals.male}</strong></TableCell>
                      <TableCell align="right"><strong>{stats.totals.female}</strong></TableCell>
                      {showUnknown && <TableCell align="right"><strong>{stats.totals.unknown}</strong></TableCell>}
                      <TableCell align="right"><strong>{stats.grand_total}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default StatisticsPage;
